import React, { useState, useEffect, useCallback } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Message } from 'primereact/message';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { classNames } from 'primereact/utils';
import establishmentService from '../services/establishmentService';
import {
    validatePAN,
    validateAadhaar,
    validateGSTIN,
    validateIFSC,
    validateAccountNumber,
    validatePersonName,
    validateRequired,
    validateMaxLength,
} from './profileComplete/validators';

/* ── Field-validation router ──────────────────────────────────────────
   Picks the right validator based on the field's name suffix. Used for
   both blur-time inline validation and step-level pre-save validation.
   Field names follow the convention used by establishmentService:
     - *_pan        → PAN regex
     - *_aadhar     → Aadhaar 12-digit
     - *_name       → person/establishment name (letters/space/dot/'-)
     - *_no_of_*    → numeric, length already capped by InputText keyfilter
     - everything else → required (when field.required) + maxLength.
   ─────────────────────────────────────────────────────────────────── */
const getFieldValidator = (field) => (value) => {
    const name = field.name || '';
    const required = !!field.required;
    const label = field.label || name;

    // Required check first — shared across all field types
    if (required && (value == null || value === '')) {
        return `${label} is required.`;
    }
    if (value == null || value === '') return ''; // optional + empty → ok

    // Type-specific checks
    if (name.endsWith('_pan')) return validatePAN(value, { required });
    if (name.endsWith('_aadhar') || name.endsWith('_aadhaar')) return validateAadhaar(value, { required });
    if (name.endsWith('_name')) return validatePersonName(value, { required, label });
    if (name.endsWith('_no_of_partners') || name.endsWith('_no_of_directors') || name.endsWith('_no_of_trustees')) {
        if (!/^\d+$/.test(String(value))) return 'Enter a valid number.';
        if (Number(value) < 1 || Number(value) > 999) return 'Enter a number between 1 and 999.';
        return '';
    }
    // Generic text → 200-char cap (free-text junk-data guard)
    return validateMaxLength(value, 200, label);
};

/* ─── GSTIN autofill mapping ──────────────────────────────────────────
   For each establishment type, which form field receives:
     • establishmentName → company / firm / brand name
     • personName        → proprietor / partner / individual name
   For company-style types (LLP, Pvt Ltd, etc.) the GSTIN response's
   `legal_name` IS the company name (not a person), so it goes into
   `establishmentName` and `personName` is left null (the user types
   the director / partner / authorized-person name themselves).
   ─────────────────────────────────────────────────────────────────── */
const TYPE_FIELD_MAP = {
    individual:       { personName:        'individual_name' /* no establishment-name field */ },
    proprietorship:   { establishmentName: 'proprietorship_establishment_name', personName: 'proprietorship_name' },
    partnership:      { establishmentName: 'partnership_establishment_name',    personName: 'partnership_partner_name' },
    llp:              { establishmentName: 'llp_name' },
    private_limited:  { establishmentName: 'pvt_ltd_name' },
    public_limited:   { establishmentName: 'public_ltd_name' },
    one_man_company:  { establishmentName: 'omc_name' },
    society:          { establishmentName: 'society_name' },
    trust:            { establishmentName: 'trust_name' },
    nbfc:             { establishmentName: 'nbfc_name' },
    bank:             { establishmentName: 'bank_name' },
    mfi:              { establishmentName: 'mfi_name' },
};

/* For these types `legal_name` from Surepass is a PERSON's name (the
   proprietor / partner / individual). For all other types, legal_name
   IS the company / entity name. */
const PERSON_LEGAL_NAME_TYPES = ['individual', 'proprietorship', 'partnership'];

/**
 * Build an establishment-form patch from a verified GSTIN payload.
 * Only fills fields the response actually supplies — empty fields stay
 * untouched so the user's manual entries (if any) aren't blown away.
 */
const buildAutofillPatch = (gst) => {
    const patch = {};
    if (!gst) return patch;

    const type = gst.type_of_establishment || null;

    // Common across every type (when the response gives them).
    if (gst.gstin) patch.gst_certificate_number = gst.gstin;
    patch.has_gst_certificate = true;
    if (type) patch.type_of_establishment = type;

    const map = type ? TYPE_FIELD_MAP[type] : null;
    if (!map) return patch;

    if (PERSON_LEGAL_NAME_TYPES.includes(type)) {
        // legal_name = person, business_name = establishment.
        if (map.establishmentName && gst.business_name) {
            patch[map.establishmentName] = gst.business_name;
        }
        if (map.personName && gst.legal_name) {
            patch[map.personName] = gst.legal_name;
        }
    } else {
        // legal_name = company name (entity itself); no person-name to fill.
        if (map.establishmentName) {
            patch[map.establishmentName] = gst.legal_name || gst.business_name || '';
        }
    }

    return patch;
};

/* maxLength cap per field-name suffix — prevents pasted junk data. */
const getMaxLength = (field) => {
    const name = field.name || '';
    if (name.endsWith('_pan')) return 10;
    if (name.endsWith('_aadhar') || name.endsWith('_aadhaar')) return 12;
    if (name.endsWith('_no_of_partners') || name.endsWith('_no_of_directors') || name.endsWith('_no_of_trustees')) return 4;
    if (name.endsWith('_name')) return 150;
    if (name === 'designation') return 100;
    return 200; // generic free-text cap
};

// PrimeReact theme ignores `p-inputtext-sm` className on Dropdown — use pt
// to apply small sizing on the trigger so it matches InputText height (~32px).
const COMPACT_TRIGGER_STYLE = { padding: '0.4375rem 0.75rem', fontSize: '0.875rem' };
const SMALL_PT = {
    root: { className: 'p-inputtext-sm' },
    input: { style: COMPACT_TRIGGER_STYLE },
    label: { style: COMPACT_TRIGGER_STYLE },
};

// SectionPanel — PrimeReact Panel with a slim custom header.
// Matches BasicInfoStep / UserTypeStep / DocumentsStep.
const SectionPanel = ({ icon, title, subtitle, children, className = '' }) => {
    const headerContent = (
        <div className="flex align-items-center gap-2">
            {icon && (
                <div
                    className="flex align-items-center justify-content-center border-round flex-shrink-0"
                    style={{
                        width: '1.75rem',
                        height: '1.75rem',
                        backgroundColor: 'var(--primary-50)',
                        color: 'var(--primary-color)',
                        fontSize: '0.85rem',
                    }}
                >
                    <i className={`pi ${icon}`} aria-hidden="true" />
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="text-900 font-semibold text-sm">{title}</div>
                {subtitle && (
                    <div className="text-500 font-normal" style={{ fontSize: '0.7rem', marginTop: '0.1rem' }}>
                        {subtitle}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <Panel
            header={headerContent}
            toggleable={false}
            className={classNames('mb-3', className)}
            pt={{
                header: { className: 'py-2 px-3' },
                content: { className: 'p-3' },
            }}
        >
            {children}
        </Panel>
    );
};

// Compact field wrapper — text-xs label, mb-1 to input, mb-2 between fields.
const Field = ({ label, required, error, hint, cols = 'col-12', children }) => (
    <div className={classNames('mb-2', cols)}>
        <label className="block text-700 text-xs font-medium mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {children}
        {hint && !error && (
            <small className="text-500 block mt-1 text-xs">{hint}</small>
        )}
        {error && (
            <small role="alert" className="p-error block mt-1 text-xs">
                {Array.isArray(error) ? error[0] : error}
            </small>
        )}
    </div>
);

const EstablishmentStep = ({
    establishmentInfo,
    setEstablishmentInfo,
    toast,
    validationErrors = {},
    basicInfo = {},
    /* When true, this step's data is the company owner's and read-only.
       Renders an info banner and visually disables the form via fieldset. */
    inherited = false,
    isCompanyEmployee = false,
}) => {
    const [establishmentTypes, setEstablishmentTypes] = useState([]);
    const [establishmentFields, setEstablishmentFields] = useState([]);
    const [loadingEstablishment, setLoadingEstablishment] = useState(false);

    /* ── GSTIN autofill state ────────────────────────────────────────
       gstinDraft holds the GSTIN currently in the input (uppercase, ≤15).
       gstinResult is the verified payload (or null). Drives the badge.
       gstinError surfaces validation / network failures inline.
       ─────────────────────────────────────────────────────────────── */
    const [gstinDraft, setGstinDraft]         = useState(establishmentInfo.gst_certificate_number || '');
    const [gstinVerifying, setGstinVerifying] = useState(false);
    const [gstinResult, setGstinResult]       = useState(null);
    const [gstinError, setGstinError]         = useState('');

    // Load establishment types on mount
    useEffect(() => {
        const loadEstablishmentTypes = async () => {
            try {
                const response = await establishmentService.getEstablishmentTypes();
                if (response.data.success) {
                    setEstablishmentTypes(response.data.data);
                }
            } catch (error) {
                console.error('Error loading establishment types:', error);
            }
        };
        loadEstablishmentTypes();
    }, []);

    // Load requirements when establishment type changes
    useEffect(() => {
        const loadRequirements = async () => {
            if (!establishmentInfo.type_of_establishment) {
                setEstablishmentFields([]);
                return;
            }

            setLoadingEstablishment(true);
            try {
                const response = await establishmentService.getEstablishmentRequirements(
                    establishmentInfo.type_of_establishment
                );

                if (response.data.success) {
                    // Get field configurations from service (this is frontend-only, doesn't need API data)
                    const fields = establishmentService.getFieldsForType(
                        establishmentInfo.type_of_establishment
                    );
                    setEstablishmentFields(fields);
                } else {
                    // Even if API fails, still load fields from service
                    console.warn('API returned error, loading fields from service:', response.data.message);
                    const fields = establishmentService.getFieldsForType(
                        establishmentInfo.type_of_establishment
                    );
                    setEstablishmentFields(fields);
                }
            } catch (error) {
                // Silently handle error and load fields from service
                console.warn('Error loading establishment requirements from API, using local configuration:', error.message);

                // Load fields from service even if API fails
                const fields = establishmentService.getFieldsForType(
                    establishmentInfo.type_of_establishment
                );
                setEstablishmentFields(fields);
            } finally {
                setLoadingEstablishment(false);
            }
        };
        loadRequirements();
    }, [establishmentInfo.type_of_establishment]);

    // Auto-fill establishment name from Basic Info when establishment type changes
    useEffect(() => {
        if (!establishmentInfo.type_of_establishment) {
            return; // Skip if no establishment type selected
        }

        // Handle Individual type separately - use first_name + last_name
        if (establishmentInfo.type_of_establishment === 'individual') {
            // Only auto-fill if the field is empty and we have first/last name
            if (!establishmentInfo.individual_name && (basicInfo.first_name || basicInfo.last_name)) {
                const fullName = `${basicInfo.first_name || ''} ${basicInfo.last_name || ''}`.trim();
                if (fullName) {
                    console.log(`[AUTO-FILL] Pre-filling individual_name with "${fullName}" from Basic Info`);
                    setEstablishmentInfo(prev => ({
                        ...prev,
                        individual_name: fullName
                    }));

                    // Show a success message to inform user
                    if (toast && toast.current) {
                        toast.current.show({
                            severity: 'info',
                            summary: 'Auto-filled',
                            detail: `Name pre-filled from Basic Info`,
                            life: 3000
                        });
                    }
                }
            }
            return;
        }

        // For other establishment types, use company_name
        if (!basicInfo.company_name) {
            return; // Skip if no company name in basic info
        }

        // Mapping of establishment types to their name fields
        const establishmentNameFieldMapping = {
            'proprietorship': 'proprietorship_establishment_name',
            'partnership': 'partnership_establishment_name',
            'llp': 'llp_name',
            'private_limited': 'pvt_ltd_name',
            'public_limited': 'public_ltd_name',
            'one_man_company': 'omc_name',
            'society': 'society_name',
            'trust': 'trust_name',
            'nbfc': 'nbfc_name',
            'bank': 'bank_name',
            'mfi': 'mfi_name'
        };

        const nameField = establishmentNameFieldMapping[establishmentInfo.type_of_establishment];

        // Only auto-fill if the field is empty (don't overwrite existing data)
        if (nameField && !establishmentInfo[nameField]) {
            console.log(`[AUTO-FILL] Pre-filling ${nameField} with "${basicInfo.company_name}" from Basic Info`);
            setEstablishmentInfo(prev => ({
                ...prev,
                [nameField]: basicInfo.company_name
            }));

            // Show a success message to inform user
            if (toast && toast.current) {
                toast.current.show({
                    severity: 'info',
                    summary: 'Auto-filled',
                    detail: `Establishment name pre-filled from Basic Info`,
                    life: 3000
                });
            }
        }
    }, [establishmentInfo.type_of_establishment, basicInfo.company_name, basicInfo.first_name, basicInfo.last_name, establishmentInfo, setEstablishmentInfo, toast]);

    // Handle field change
    const handleFieldChange = (fieldName, value) => {
        setEstablishmentInfo(prev => ({
            ...prev,
            [fieldName]: value
        }));
    };

    /* ── GSTIN verify handler ───────────────────────────────────────
       Sends the current draft to /establishment/gstin-verify. Backend
       is cache-first, so repeat calls for the same number cost us
       nothing on the Surepass side. On success, autofill the form via
       buildAutofillPatch — only fields the response actually supplies
       get written, so any manual entries the user already typed for
       the OTHER fields (designation, no-of-directors, etc.) survive.
       ─────────────────────────────────────────────────────────────── */
    const handleVerifyGstin = useCallback(async () => {
        const value = (gstinDraft || '').toUpperCase().trim();
        const formatErr = validateGSTIN(value, { required: true });
        if (formatErr) {
            setGstinError(formatErr);
            return;
        }
        setGstinError('');
        setGstinVerifying(true);
        try {
            const res = await establishmentService.verifyGstin(value);
            if (!res.success) {
                setGstinResult(null);
                setGstinError(res.message || 'Could not verify GSTIN. Please retry.');
                toast?.current?.show?.({
                    severity: 'error',
                    summary:  'Verification failed',
                    detail:   res.message || 'Please check the GSTIN and retry.',
                    life:     3500,
                });
                return;
            }

            const data = res.data || {};
            setGstinResult(data);

            if (!data.is_active) {
                // Block autofill on Cancelled / Suspended GSTINs — but
                // still echo the GSTIN itself into the form so it gets
                // saved (the user's GST cert may be stale).
                setEstablishmentInfo((prev) => ({
                    ...prev,
                    gst_certificate_number: data.gstin || value,
                    has_gst_certificate:    true,
                }));
                toast?.current?.show?.({
                    severity: 'warn',
                    summary:  `GSTIN ${data.gstin_status || 'not active'}`,
                    detail:   'This GSTIN is not active. Please use a different one.',
                    life:     5000,
                });
                return;
            }

            // Active → apply the autofill patch.
            const patch = buildAutofillPatch(data);
            setEstablishmentInfo((prev) => ({ ...prev, ...patch }));
            toast?.current?.show?.({
                severity: 'success',
                summary:  res.cached ? 'Already verified' : 'GSTIN verified',
                detail:   `${data.business_name || data.legal_name || 'Details'} auto-filled below.`,
                life:     2500,
            });
        } catch (e) {
            setGstinResult(null);
            setGstinError('Could not verify GSTIN. Please retry.');
        } finally {
            setGstinVerifying(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gstinDraft]);

    // Local error state for fields validated on blur — falls through to
    // parent validationErrors prop so the shell's submit-time guard wins.
    const [localErrors, setLocalErrors] = useState({});

    const errorFor = (fieldName) => localErrors[fieldName] || validationErrors[fieldName] || '';

    const handleBlur = (field, value) => {
        const validator = getFieldValidator(field);
        const err = validator(value);
        setLocalErrors((prev) => ({ ...prev, [field.name]: err }));
    };

    // Render dynamic field — compact rhythm matching the rest of the wizard.
    const renderField = (field) => {
        const value = establishmentInfo[field.name] || '';
        const error = errorFor(field.name);

        // Check if field should be shown based on conditional
        if (field.conditional && !establishmentInfo[field.conditional]) {
            return null;
        }

        if (field.type === 'checkbox') {
            return (
                <div className="col-12 mb-2" key={field.name}>
                    <div className="flex align-items-center gap-2">
                        <Checkbox
                            inputId={field.name}
                            checked={value === true}
                            onChange={(e) => handleFieldChange(field.name, e.checked)}
                        />
                        <label htmlFor={field.name} className="text-700 text-xs cursor-pointer">
                            {field.label}
                        </label>
                    </div>
                </div>
            );
        }

        const isPan = field.name.endsWith('_pan');
        const isAadhaar = field.name.endsWith('_aadhar') || field.name.endsWith('_aadhaar');
        const isNumeric = field.type === 'number'
            || field.name.endsWith('_no_of_partners')
            || field.name.endsWith('_no_of_directors')
            || field.name.endsWith('_no_of_trustees');

        return (
            <Field
                key={field.name}
                label={field.label}
                required={field.required}
                error={error}
                cols="col-12 sm:col-6 md:col-4"
            >
                <InputText
                    id={field.name}
                    value={value}
                    onChange={(e) => {
                        let v = e.target.value;
                        if (isPan) v = v.toUpperCase().slice(0, 10);
                        if (isAadhaar) v = v.replace(/\D/g, '').slice(0, 12);
                        handleFieldChange(field.name, v);
                        // Clear error as user types once an error is showing
                        if (errorFor(field.name)) {
                            const validator = getFieldValidator(field);
                            setLocalErrors((prev) => ({ ...prev, [field.name]: validator(v) }));
                        }
                    }}
                    onBlur={() => handleBlur(field, value)}
                    placeholder={`Enter ${(field.label || '').toLowerCase()}`}
                    pattern={field.pattern}
                    keyfilter={isNumeric ? 'int' : isAadhaar ? 'int' : undefined}
                    maxLength={getMaxLength(field)}
                    inputMode={isNumeric || isAadhaar ? 'numeric' : 'text'}
                    autoCapitalize={isPan ? 'characters' : field.name.endsWith('_name') ? 'words' : 'sentences'}
                    aria-invalid={!!error}
                    className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!error })}
                />
            </Field>
        );
    };

    /* ── Employee-mode banner ──────────────────────────────────────────
       When `inherited` is true the data shown is the company owner's, not
       this user's. Show a clear message and disable every form control via
       the wrapping <fieldset disabled>. */
    if (isCompanyEmployee && !inherited) {
        return (
            <div>
                <Message
                    severity="warn"
                    className="w-full mb-3"
                    content={(
                        <div className="flex align-items-start gap-2">
                            <i className="pi pi-info-circle mt-1" />
                            <div>
                                <div className="font-medium">Waiting for your company admin</div>
                                <small className="text-700">
                                    Your company hasn't filled the establishment details yet. You can continue with
                                    the rest of the profile — the establishment section will be inherited
                                    automatically once it's filled.
                                </small>
                            </div>
                        </div>
                    )}
                />
            </div>
        );
    }

    return (
        <div>
            {inherited && (
                <Message
                    severity="info"
                    className="w-full mb-3"
                    content={(
                        <div className="flex align-items-start gap-2">
                            <i className="pi pi-building mt-1" />
                            <div>
                                <div className="font-medium">Inherited from your company</div>
                                <small className="text-700">
                                    These details belong to your company and are read-only here.
                                    Ask your company admin to update them if anything is wrong.
                                </small>
                            </div>
                        </div>
                    )}
                />
            )}

            <fieldset
                disabled={inherited}
                style={inherited ? { border: 0, padding: 0, margin: 0, opacity: 0.85 } : { border: 0, padding: 0, margin: 0 }}
            >
            {/* ─ Section A — GST FIRST (drives autofill for the rest) ── */}
            <SectionPanel
                icon="pi-id-card"
                title="GST registration"
                subtitle="If you have a GSTIN, verify it to auto-fill the establishment details below."
            >
                <div className="flex align-items-center gap-2 mb-2">
                    <Checkbox
                        inputId="has_gst_certificate"
                        checked={establishmentInfo.has_gst_certificate || false}
                        onChange={(e) => {
                            handleFieldChange('has_gst_certificate', e.checked);
                            if (!e.checked) {
                                handleFieldChange('gst_certificate_number', '');
                                setGstinDraft('');
                                setGstinResult(null);
                                setGstinError('');
                            }
                        }}
                    />
                    <label htmlFor="has_gst_certificate" className="text-700 text-xs cursor-pointer">
                        I have GST registration
                    </label>
                </div>

                {establishmentInfo.has_gst_certificate && (
                    <>
                        <div className="grid align-items-end">
                            <Field
                                label="GSTIN"
                                required
                                error={gstinError || errorFor('gst_certificate_number')}
                                cols="col-12 sm:col-8 md:col-6"
                            >
                                <InputText
                                    id="gst_certificate_number"
                                    value={gstinDraft}
                                    onChange={(e) => {
                                        const v = e.target.value.toUpperCase().slice(0, 15);
                                        setGstinDraft(v);
                                        // Mirror into the saved field so a manual
                                        // submit without verifying still preserves
                                        // what the user typed.
                                        handleFieldChange('gst_certificate_number', v);
                                        if (gstinError) setGstinError('');
                                        // If user edits to a different GSTIN, drop
                                        // the old verified badge — it's stale now.
                                        if (gstinResult && gstinResult.gstin !== v) {
                                            setGstinResult(null);
                                        }
                                    }}
                                    onBlur={() => setLocalErrors((p) => ({
                                        ...p,
                                        gst_certificate_number: validateGSTIN(gstinDraft, { required: true }),
                                    }))}
                                    placeholder="22AAAAA0000A1Z5"
                                    maxLength={15}
                                    autoCapitalize="characters"
                                    aria-invalid={!!(gstinError || errorFor('gst_certificate_number'))}
                                    className={classNames('w-full p-inputtext-sm', {
                                        'p-invalid': !!(gstinError || errorFor('gst_certificate_number')),
                                    })}
                                />
                            </Field>
                            <div className="col-12 sm:col-4 md:col-3 mb-2">
                                <Button
                                    label={gstinVerifying ? 'Verifying…' : 'Verify & autofill'}
                                    icon={gstinVerifying ? 'pi pi-spin pi-spinner' : 'pi pi-check-circle'}
                                    loading={gstinVerifying}
                                    onClick={handleVerifyGstin}
                                    size="small"
                                    className="w-full"
                                />
                            </div>
                        </div>

                        {/* Verified badge — only shows after a successful Active verification. */}
                        {gstinResult && gstinResult.is_active && (
                            <div className="flex flex-wrap align-items-center gap-2 mt-1">
                                <Tag
                                    icon="pi pi-check"
                                    severity="success"
                                    value={`GSTIN Active${gstinResult.date_of_registration ? ` since ${gstinResult.date_of_registration.slice(0, 4)}` : ''}`}
                                    className="text-xs"
                                />
                                {gstinResult.constitution_of_business && (
                                    <Tag
                                        severity="info"
                                        value={gstinResult.constitution_of_business}
                                        className="text-xs"
                                    />
                                )}
                                {(gstinResult.business_name || gstinResult.legal_name) && (
                                    <small className="text-600">
                                        {gstinResult.business_name || gstinResult.legal_name}
                                    </small>
                                )}
                            </div>
                        )}

                        {/* Inactive warning */}
                        {gstinResult && !gstinResult.is_active && (
                            <Message
                                severity="warn"
                                text={`This GSTIN is ${gstinResult.gstin_status || 'not active'}. Please use a different one.`}
                                className="w-full mt-2"
                            />
                        )}
                    </>
                )}
            </SectionPanel>

            {/* ─ Section B — Establishment ─────────────────────────── */}
            <SectionPanel
                icon="pi-building"
                title="Establishment information"
                subtitle="Business details and required documents."
            >
                <div className="grid">
                    <Field
                        label="Type of establishment"
                        required
                        error={validationErrors.type_of_establishment}
                        cols="col-12 sm:col-6 md:col-4"
                    >
                        <Dropdown
                            id="establishment_type"
                            value={establishmentInfo.type_of_establishment}
                            options={establishmentTypes}
                            onChange={(e) => handleFieldChange('type_of_establishment', e.value)}
                            placeholder="Select type"
                            className={classNames('w-full', { 'p-invalid': !!validationErrors.type_of_establishment })}
                            optionLabel="label"
                            optionValue="value"
                            pt={SMALL_PT}
                        />
                    </Field>
                </div>

                {/* Dynamic fields based on establishment type */}
                {loadingEstablishment ? (
                    <div className="flex align-items-center gap-2 text-500 text-sm py-3">
                        <i className="pi pi-spin pi-spinner" />
                        <span>Loading establishment requirements…</span>
                    </div>
                ) : establishmentFields.length > 0 ? (
                    <div className="grid">
                        {establishmentFields.map((field) => renderField(field))}
                    </div>
                ) : establishmentInfo.type_of_establishment && (
                    <Message
                        severity="info"
                        text="No additional fields required for this establishment type."
                        className="w-full mt-2"
                    />
                )}
            </SectionPanel>

            {/* ─ Section C — Bank account (optional) ───────────────── */}
            {establishmentInfo.type_of_establishment && (
                <SectionPanel
                    icon="pi-wallet"
                    title="Bank account details"
                    subtitle="Optional — banking information for transactions, can be added later."
                >
                    <div className="grid">
                        <Field
                            label="Account holder name"
                            error={errorFor('bank_account_name')}
                            cols="col-12 sm:col-6 md:col-4"
                        >
                            <InputText
                                id="bank_account_name"
                                value={establishmentInfo.bank_account_name || ''}
                                onChange={(e) => {
                                    handleFieldChange('bank_account_name', e.target.value);
                                    if (errorFor('bank_account_name')) {
                                        setLocalErrors((p) => ({
                                            ...p,
                                            bank_account_name: validatePersonName(e.target.value, { label: 'Account holder name' }),
                                        }));
                                    }
                                }}
                                onBlur={() => setLocalErrors((p) => ({
                                    ...p,
                                    bank_account_name: validatePersonName(establishmentInfo.bank_account_name, { label: 'Account holder name' }),
                                }))}
                                placeholder="As per bank records"
                                maxLength={100}
                                autoCapitalize="words"
                                aria-invalid={!!errorFor('bank_account_name')}
                                className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errorFor('bank_account_name') })}
                            />
                        </Field>

                        <Field
                            label="Account number"
                            error={errorFor('account_number')}
                            cols="col-12 sm:col-6 md:col-4"
                        >
                            <InputText
                                id="account_number"
                                value={establishmentInfo.account_number || ''}
                                onChange={(e) => {
                                    const v = e.target.value.replace(/\D/g, '').slice(0, 18);
                                    handleFieldChange('account_number', v);
                                    if (errorFor('account_number')) {
                                        setLocalErrors((p) => ({
                                            ...p,
                                            account_number: validateAccountNumber(v),
                                        }));
                                    }
                                }}
                                onBlur={() => setLocalErrors((p) => ({
                                    ...p,
                                    account_number: validateAccountNumber(establishmentInfo.account_number),
                                }))}
                                placeholder="9 to 18 digits"
                                keyfilter="int"
                                maxLength={18}
                                inputMode="numeric"
                                aria-invalid={!!errorFor('account_number')}
                                className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errorFor('account_number') })}
                            />
                        </Field>

                        <Field
                            label="Account type"
                            error={errorFor('account_type')}
                            cols="col-12 sm:col-6 md:col-4"
                        >
                            <Dropdown
                                id="account_type"
                                value={establishmentInfo.account_type || ''}
                                options={[
                                    { label: 'Savings', value: 'savings' },
                                    { label: 'Current', value: 'current' },
                                    { label: 'Overdraft', value: 'overdraft' },
                                    { label: 'Cash Credit', value: 'cash_credit' },
                                    { label: 'NRI', value: 'nri' },
                                ]}
                                onChange={(e) => handleFieldChange('account_type', e.value)}
                                placeholder="Select"
                                className={classNames('w-full', { 'p-invalid': !!errorFor('account_type') })}
                                optionLabel="label"
                                optionValue="value"
                                pt={SMALL_PT}
                            />
                        </Field>

                        <Field
                            label="IFSC code"
                            error={errorFor('ifsc_code')}
                            cols="col-12 sm:col-6 md:col-4"
                        >
                            <InputText
                                id="ifsc_code"
                                value={establishmentInfo.ifsc_code || ''}
                                onChange={(e) => {
                                    const v = e.target.value.toUpperCase().slice(0, 11);
                                    handleFieldChange('ifsc_code', v);
                                    if (errorFor('ifsc_code')) {
                                        setLocalErrors((p) => ({
                                            ...p,
                                            ifsc_code: validateIFSC(v),
                                        }));
                                    }
                                }}
                                onBlur={() => setLocalErrors((p) => ({
                                    ...p,
                                    ifsc_code: validateIFSC(establishmentInfo.ifsc_code),
                                }))}
                                placeholder="HDFC0001234"
                                maxLength={11}
                                autoCapitalize="characters"
                                aria-invalid={!!errorFor('ifsc_code')}
                                className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errorFor('ifsc_code') })}
                            />
                        </Field>

                        <Field
                            label="Bank name"
                            error={errorFor('bank_name')}
                            cols="col-12 sm:col-6 md:col-4"
                        >
                            <InputText
                                id="bank_name"
                                value={establishmentInfo.bank_name || ''}
                                onChange={(e) => handleFieldChange('bank_name', e.target.value)}
                                placeholder="HDFC Bank"
                                maxLength={150}
                                autoCapitalize="words"
                                aria-invalid={!!errorFor('bank_name')}
                                className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errorFor('bank_name') })}
                            />
                        </Field>
                    </div>
                </SectionPanel>
            )}
            </fieldset>
        </div>
    );
};

/* ── Step-level validation (used by the wizard shell at submit time) ──
   Returns an errors object keyed by field name. Empty object = valid. */
EstablishmentStep.runValidations = (establishmentInfo) => {
    const errors = {};
    if (!establishmentInfo?.type_of_establishment) {
        errors.type_of_establishment = 'Establishment type is required.';
    }

    // Dynamic fields (validate based on the field config for the chosen type)
    if (establishmentInfo?.type_of_establishment) {
        const fields = establishmentService.getFieldsForType(establishmentInfo.type_of_establishment) || [];
        fields.forEach((field) => {
            if (field.type === 'checkbox') return;
            if (field.conditional && !establishmentInfo[field.conditional]) return;
            const v = establishmentInfo[field.name];
            const validator = getFieldValidator(field);
            const err = validator(v);
            if (err) errors[field.name] = err;
        });
    }

    // GST number — required when has_gst_certificate is true
    if (establishmentInfo?.has_gst_certificate) {
        const e = validateGSTIN(establishmentInfo.gst_certificate_number, { required: true });
        if (e) errors.gst_certificate_number = e;
    }

    // Bank fields — only validate when the user has started entering bank details.
    // Account holder name has only soft format check; the others get full regex
    // validation when present.
    const anyBankField = establishmentInfo?.bank_account_name
        || establishmentInfo?.account_number
        || establishmentInfo?.account_type
        || establishmentInfo?.ifsc_code
        || establishmentInfo?.bank_name;
    if (anyBankField) {
        const accErr = validateAccountNumber(establishmentInfo?.account_number);
        if (accErr) errors.account_number = accErr;
        const ifscErr = validateIFSC(establishmentInfo?.ifsc_code);
        if (ifscErr) errors.ifsc_code = ifscErr;
        const nameErr = validatePersonName(establishmentInfo?.bank_account_name, { label: 'Account holder name' });
        if (nameErr) errors.bank_account_name = nameErr;
    }

    return errors;
};

EstablishmentStep.hasErrors = (errors) =>
    !!errors && Object.values(errors).some((e) => e && e !== '');

export default EstablishmentStep;